/* eslint-disable import/prefer-default-export */
import ky, { Options } from 'ky';
import Cookie from 'js-cookie';
import config from 'config';
import { Settings } from 'CoreApi.Settings_V12';
import { useAuthenticatedApiClient } from 'utils/apiClient/base';
import { mergeDeepRight } from 'ramda';
import { SavePayload } from 'Settings/components/views/BusinessSettingsForm';
import { requestUpload } from 'utils/api';
import {
  LoginUserBody,
  LoginApiCallArguments,
  PinLoginResponse,
  Token,
  PostFormSubmissionArguments,
} from 'utils/apiClient/services/core/types';
import ownFetch from 'utils/own-fetch';
import { HubSpot } from 'Widgets/HubSpot';

const clientOpts: Options = {
  prefixUrl: `${config.CORE_URL}/api`,
  headers: {
    'X-API-Version': '12',
  },
};

const publicClient = ky.create(clientOpts);

export const publicClientMethods = {
  loginUser: (
    { accountToken, ...credentials }: LoginUserBody,
    opts: Options = {}
  ) =>
    publicClient
      .post(
        'auth/pin-login/',
        mergeDeepRight(
          {
            json: credentials,
            headers: { Authorization: `Token ${accountToken}` },
          },
          opts
        ) as Options
      )
      .json<PinLoginResponse>(),
};

export async function postFormSubmission({
  url,
  formData,
}: PostFormSubmissionArguments) {
  const form = document.createElement('form');
  form.setAttribute('method', 'post');
  form.setAttribute('action', url);

  Object.keys(formData).forEach((key) => {
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', formData[key]);
    form.appendChild(hiddenField);
  });
  document.body.appendChild(form);
  form.submit();
}

export const usePOSCoreApiClient = () => {
  const authenticatedApiClient = useAuthenticatedApiClient();

  const privateClient = authenticatedApiClient.extend(clientOpts);

  return {
    ...publicClientMethods,

    fetchAccount: (opts: Options = {}) =>
      privateClient.get('account/', opts).json<Settings>(),
    saveAccount: (settingsData: SavePayload) =>
      privateClient.post('account/', {
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(settingsData),
      }),
    uploadTwintImage: async (file: File, userToken: string) => {
      const url = `${config.CORE_URL}/api/account/twint_image/`;
      const formData = { image: file };
      return requestUpload({
        url,
        userToken,
        requestData: formData,
      });
    },
    loginAccount: async ({ email, password }: LoginApiCallArguments) => {
      const url = `${config.CORE_URL}/api/auth/login/`;
      const body = { email, password };

      const response = await fetch(url, {
        method: 'post',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const { token, error } = await response.json();

      if (response.ok) {
        return {
          error: null,
          token,
        };
      }
      return { token: null, error };
    },
    loginCookie: async ({ email, password }: LoginApiCallArguments) => {
      const url = `${config.CORE_URL}/api/auth/`;
      const data = await ownFetch(url, 'POST', { email, password });
      return data.token;
    },
    loginUser: async (params: {
      username: string;
      pin: string;
      accountToken: string;
    }) => {
      const url = `${config.CORE_URL}/api/auth/pin-login/`;
      const { username, pin, accountToken } = params;
      const body = { username, pin };
      const response = await fetch(url, {
        method: 'post',
        headers: {
          Authorization: `Token ${accountToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const { token, error } = await response.json();
      if (response.ok) {
        return {
          error: null,
          token,
        };
      }
      return { token: null, error };
    },
    logout: async () => {
      HubSpot.resetWidget();
      const url = `${config.CORE_URL}/api/auth/logout/`;
      const CSRFToken = Cookie.get('csrftoken');
      if (CSRFToken) {
        await fetch(url, {
          method: 'POST',
          headers: {
            'X-CSRFToken': CSRFToken,
          },
        });
      }
    },
    requestNewPassword: async ({
      email,
    }: Omit<LoginApiCallArguments, 'password'>) => {
      const url = `${config.CORE_URL}/api/auth/password-reset-token/`;
      return ownFetch(url, 'POST', { email });
    },
    resetPassword: async ({
      password,
      token,
    }: Omit<LoginApiCallArguments, 'email'> & Token) => {
      const url = `${config.CORE_URL}/api/auth/password-reset/`;
      return ownFetch(url, 'POST', { password, token });
    },
  };
};
